import { Box, Button, Stack, Flex, Badge } from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import Error from 'next/error';
import Router, { useRouter } from 'next/router';
import React from 'react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Maybe, TermCategory } from '../../generated/types';
import useIsMobile from '../../hooks/useIsMobile';
import {
  applyMapParamToRoute,
  autocompleteResultToUrl,
} from '../../utils/helper';
import Container from '../Layout/Container';
import RadiusSelect from './RadiusSelect';
import SearchCategoryPicker from './SearchCategoryPicker';
import BetterPlaceAutocomplete, {
  TransformedAutocompleteData,
} from './BetterPlaceAutocomplete';
import { FilterIcon2 } from '../../config/icons';

interface SearchFormProps {
  radius: number;
  query: string;
  location?: {
    lat: number;
    lng: number;
  };
  categories: TermCategory[];
  activeCategory?: TermCategory | null;
  filters?: Maybe<{
    label: string;
    field: string;
    value: string;
  }>[];
  onOpenDrawer: () => void;
}

export default function SearchForm({
  radius,
  query,
  location,
  categories,
  activeCategory,
  filters,
  onOpenDrawer,
}: SearchFormProps) {
  const isMobile = useIsMobile();
  const router = useRouter();
  const [radiusValue, setRadius] = useState<number>(radius);

  const [
    selectedItem,
    setSelectedItem,
  ] = useState<TransformedAutocompleteData>();

  useEffect(() => {
    if (!radiusValue && !router.isFallback) {
      setRadius(radius);
    }
  }, [radiusValue, router.isFallback]);

  const hasMapParam =
    router.query && (router.query.karte === '' || router.query.karte === '1')
      ? true
      : false;

  if (!activeCategory) {
    return <Error statusCode={404} />;
  }

  const getSearchUrl = (category: TermCategory, additionalProps = {}) => {
    if (selectedItem) {
      return autocompleteResultToUrl(category, selectedItem, radiusValue + '');
    } else {
      return autocompleteResultToUrl(category, selectedItem, radiusValue + '', {
        filter: router.query.filter,
        latitude: router.query.latitude,
        longitude: router.query.longitude,
        query: router.query.query,
        radius: radiusValue + '',
        ...additionalProps,
      });
    }
  };

  const onChangeCategory = (category: TermCategory) => {
    if (category.id !== activeCategory.id) {
      const searchUrl = getSearchUrl(category, {
        sort: 'popularity',
        sortOrder: 'desc',
        radius: null,
      });

      // Always set sorting to pop

      if (searchUrl) {
        Router.push(applyMapParamToRoute(searchUrl, hasMapParam));
      }
    }
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const searchUrl = getSearchUrl(activeCategory);
    if (searchUrl) {
      Router.push(applyMapParamToRoute(searchUrl, hasMapParam));
    }
  };

  const onChangeRadius = (e: ChangeEvent<HTMLSelectElement>) => {
    setRadius(parseInt(e.target.value, 10));
  };

  const onSelectItem = (item: TransformedAutocompleteData) => {
    setSelectedItem(item);
  };

  return (
    <Box
      bg="gray.100"
      paddingY={4}
      borderBottom="1px"
      borderBottomColor="gray.200"
    >
      <Container>
        <Box>
          <form onSubmit={onSubmit}>
            <Stack isInline={!isMobile} spacing={3}>
              <SearchCategoryPicker
                categories={categories}
                activeCategory={activeCategory}
                onChangeCategory={onChangeCategory}
              />
              <Flex align="center" flex={1}>
                <Box marginRight={3}>in</Box>
                <BetterPlaceAutocomplete
                  category={activeCategory.frontendUrl!}
                  id={`place-autocomplete-${activeCategory.id}`}
                  initialValue={query}
                  placeholder={activeCategory.question}
                  onSelectItem={onSelectItem}
                />
              </Flex>
              {(selectedItem && selectedItem.type === 'geocode') ||
              (!selectedItem && location && radius) ? (
                <RadiusSelect
                  value={radiusValue + ''}
                  onChangeValue={onChangeRadius}
                />
              ) : (
                <Button
                  variant="outline"
                  marginRight={2}
                  onClick={onOpenDrawer}
                >
                  <FilterIcon2 marginRight={2} />
                  Filter
                  {filters && filters.length > 0 && (
                    <Badge marginLeft={1} colorScheme="red">
                      {filters.length}
                    </Badge>
                  )}
                </Button>
              )}

              <Button
                variant="outline"
                colorScheme="brand"
                type="submit"
                isDisabled={!selectedItem && !location}
              >
                <SearchIcon marginRight={2} />
                Suchen
              </Button>
            </Stack>
          </form>
        </Box>
      </Container>
    </Box>
  );
}
